import { useEffect, useState } from "react";

import { Button, Form, Input, message, Switch, Radio } from "antd";

import editIcon from "data/icons/edit-blue.svg";
import api from "services/api.service";
import { Snackbar } from "components/common";

import "./EmailSender.scss";

const EmailSender = () => {
  const [ form ] = Form.useForm();

  const [ error, setError ] = useState("");
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isEditMode, setIsEditMode ] = useState(false);
  const [ isEnabledLoading, setIsEnabledLoading ] = useState(false);
  const [ accessError, setAccessError ] = useState<string | null>(null);
  const [ showPasswordInput, setShowPasswordInput ] = useState(false);

  const [ id, setId ] = useState();
  const [ taskId, setTaskId ] = useState("");
  const [ status, setStatus ] = useState<{ status: string; message: string; }>();
  const [ emailSettings, setEmailSettings ] = useState<any>(null);

  const areRequiredFieldsFilled = () => {
    return emailSettings?.host &&
      emailSettings?.port &&
      emailSettings?.host_user
      // && emailSettings?.host_password;
  };

  const onCheckStatus = async (taskId: string) => {
    try {
      const response = await api.asyncTasksCheck(taskId);

      setStatus(response.data);
    } catch (e) {
      setError("Error");
    }
  };

  const onCheck = async () => {
    try {
      const response = await api.getSettingsEmailSenderCheck();

      setTaskId(response.data.task_id);

      setTimeout(() => {
        onCheckStatus(response.data.task_id);
      }, 1000);
    } catch (e: any) {
      setError(e.response.data.detail);
    }
  };

  const handleEnabledChange = async (checked: boolean) => {
    try {
      setIsEnabledLoading(true);

      const currentValues = form.getFieldsValue();
      const updatedValues = { ...currentValues, enabled: checked };

      const response = id
        ? await api.updateSettingsEmailSender(updatedValues)
        : await api.createSettingsEmailSender(updatedValues);

      if (response.data) {
        message.success("Settings updated");
        setError("");
        onLoad();
      }
    } catch (e: any) {
      setError(e.response.data.error || "Something went wrong!")
      message.error(e.response.data.error || e.error || "Something went wrong!");
    } finally {
      setIsEnabledLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);

      const { security_type, ...otherValues } = values;
      const processedValues = {
        ...otherValues,
        use_ssl: security_type === "ssl",
        use_tls: security_type === "tls",
      };

      const response = id
        ? await api.updateSettingsEmailSender(processedValues)
        : await api.createSettingsEmailSender(processedValues);

      if (response.data) {
        message.success("Settings updated");
        setError("");
        setIsEditMode(false);
        onLoad();
      }

      setIsLoading(false);
    } catch (e: any) {
      setError(e.response.data.error || "Something went wrong!")
      setIsLoading(false);
      message.error(e.response.data.error || e.error || "Something went wrong!");
    }
  }

  const onLoad = async () => {
    try {
      const response = await api.getSettingsEmailSender();

      if (response.data.id) {
        const { use_tls, ...otherData } = response.data;
        let security_type = "ssl";
        if (use_tls) {
          security_type = "tls";
        }

        form.setFieldsValue({
          ...otherData,
          security_type,
        });
        setId(response.data.id);
        setEmailSettings(response.data);
        setShowPasswordInput(false);
      } else {
        setAccessError("No email settings found");
      }
    } catch (e: any) {
      if (e.response?.status === 403) {
        const errorMessage = "Only administrators have access to perform this action.";

        // temp, delete after fix api response
        setAccessError(errorMessage);

        // use it after fix api response
        // setAccessError(e.response?.data?.detail || errorMessage);
      } else {
        setError("Error loading email settings");
      }
    }
  };

  useEffect(() => {
    onLoad();
  }, []);

  if (accessError) {
    return (
      <Snackbar type="info">
        {accessError}
      </Snackbar>
    );
  }

  if (!emailSettings) {
    return null;
  }

  return (
    <div>
      { isEditMode ? (
        <div className="section">
          <div className="section-header">
            <span>Email Sender Settings</span>
          </div>

          <Form
            className="email-sender"
            layout="vertical"
            onFinish={ handleSubmit }
            form={ form }
          >
            { error && (<div className="login-error">{ error }</div>) }

            <Form.Item
              name="host"
              label="Host"
              rules={ [ { required: true, message: "Required" } ] }
            >
              <Input
                placeholder="smtp.yandex.com"
              />
            </Form.Item>

            <Form.Item
              name="port"
              label="Port"
              rules={ [ { required: true, message: "Required" } ] }
            >
              <Input
                placeholder="465"
              />
            </Form.Item>

            <Form.Item
              name="host_user"
              label="Host user"
              rules={ [ { required: true, message: "Required" } ] }
            >
              <Input
                placeholder="test@testcomp.com"
              />
            </Form.Item>

            { showPasswordInput ? (
              <Form.Item
                name="host_password"
                label="Host password"
                rules={ [ { required: true, message: "Required" } ] }
              >
                <Input
                  placeholder=""
                />
              </Form.Item>
            ) : (
              <div style={ { marginBottom: "24px" } }>
                <div style={ { marginBottom: "8px" } }>
                  Host password <span style={ { color: emailSettings.required_password ? "#ff4d4f" : "#0B404B" } }>*</span>
                </div>
                <Button
                  type="primary"
                  onClick={ () => setShowPasswordInput(true) }
                >
                  { emailSettings.required_password ? "Set password" : "Change password" }
                </Button>
              </div>
            ) }

            <Form.Item
              name="security_type"
              label="Security"
            >
              <Radio.Group>
                <Radio value="ssl">Use SSL</Radio>
                <Radio value="tls">Use TLS</Radio>
              </Radio.Group>
            </Form.Item>

            <div className="section-buttons">
              <Button
                style={ { width: "120px" } }
                onClick={ () => {
                  setIsEditMode(false);
                  setShowPasswordInput(false);
                } }
              >
                Cancel
              </Button>

              <Button
                htmlType="submit"
                type="primary"
                style={ { width: "120px" } }
                disabled={ isLoading }
              >
                Save
              </Button>
            </div>
          </Form>
        </div>
      ) : (
        <div className="section">
          <div className="section-header">
            <div style={ { display: "flex", flexDirection: "column", gap: "16px" } }>
              <div style={ { display: "flex", alignItems: "center", gap: "8px" } }>
                <Switch
                  checked={ emailSettings.enabled }
                  onChange={ handleEnabledChange }
                  disabled={ isEnabledLoading }
                />
                <span>{ emailSettings.enabled ? "Enable" : "Disable" }</span>
              </div>
              <span className="section-title">
                Email Sender Settings
              </span>
            </div>

            <div style={ { display: "flex", alignItems: "center", gap: "16px" } }>
              <Button
                className="edit-button"
                disabled={ !emailSettings.enabled }
                onClick={ () => setIsEditMode(true) }
              >
                Edit
                <img src={ editIcon } alt=""/>
              </Button>
            </div>
          </div>

          <div className="section-row">
            <div className="section-group">
              <div className="section-group-title">
                Host
              </div>
              <div className="section-group-text">
                { emailSettings.host ? "••••••••" : "-" }
              </div>
            </div>

            <div className="section-group">
              <div className="section-group-title">
                Port
              </div>
              <div className="section-group-text">
                { emailSettings.port ? "••••••••" : "-" }
              </div>
            </div>
          </div>

          <div className="section-row">
            <div className="section-group">
              <div className="section-group-title">
                Host User
              </div>
              <div className="section-group-text">
                { emailSettings.host_user ? "••••••••" : "-" }
              </div>
            </div>

            <div className="section-group">
              <div className="section-group-title">
                Security
              </div>
              <div className="section-group-text">
                Use { emailSettings.use_tls ? "TLS" : "SSL" }
              </div>
            </div>
          </div>

          <div className="section-buttons">
            <Button
              type="default"
              onClick={ onCheck }
              disabled={ !areRequiredFieldsFilled() || !emailSettings.enabled }
            >
              Check status
            </Button>

            <span>
              ⓘ An attempt will be made to send a test email to your address.
            </span>
          </div>
        </div>
      ) }

      { status?.status && (
        <div className="section">
          <div className="section-header">
            <span>Test Results</span>
          </div>

          <div>
            We have attempted to send you a message to your email. Check the status of this process below. <br/>
            If status is SUCCESS - check please your email inbox.
          </div>

          <Button
            type="default"
            onClick={ () => onCheckStatus(taskId) }
            style={ { margin: "10px 0" } }
          >
            Refresh
          </Button>

          <div className="section-row">
            <div className="section-group">
              <div className="section-group-title">
                Status
              </div>
              <div className="section-group-text">
                { status?.status }
              </div>
            </div>

            <div className="section-group">
              <div className="section-group-title">
                Message
              </div>
              <div className="section-group-text">
                { status?.message }
              </div>
            </div>
          </div>
        </div>
      ) }
    </div>
  );
};

export default EmailSender;
